import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Eye, Users, Smartphone, TrendingUp } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/ziyaretciler")({
  component: Page,
});

type Visit = {
  id: string;
  device_id: string | null;
  path: string;
  referrer: string | null;
  platform: string | null;
  browser: string | null;
  is_pwa: boolean;
  created_at: string;
};

function top(rows: Visit[], key: (v: Visit) => string | null, limit = 8) {
  const map = new Map<string, number>();
  for (const r of rows) {
    const k = key(r);
    if (!k) continue;
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
}

function Page() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-visits"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_visits")
        .select("id, device_id, path, referrer, platform, browser, is_pwa, created_at")
        .order("created_at", { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as Visit[];
    },
  });

  const totalQ = useQuery({
    queryKey: ["admin-visits-total"],
    refetchInterval: 30000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("site_visits")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
  });

  const rows = data ?? [];
  const now = Date.now();
  const today = rows.filter((r) => now - new Date(r.created_at).getTime() < 864e5);
  const week = rows.filter((r) => now - new Date(r.created_at).getTime() < 7 * 864e5);
  const uniqueDevices = new Set(rows.map((r) => r.device_id).filter(Boolean)).size;
  const pwaVisits = rows.filter((r) => r.is_pwa).length;

  const cards = [
    { label: "Ümumi baxış", value: totalQ.data ?? rows.length, icon: Eye },
    { label: "Bugün", value: today.length, icon: TrendingUp },
    { label: "Unikal ziyarətçi", value: uniqueDevices, icon: Users },
    { label: "PWA-dan giriş", value: pwaVisits, icon: Smartphone },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Ziyarətçi statistikası"
        description="Sayta daxil olan istifadəçilər, ən çox baxılan səhifələr və mənbələr"
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <c.icon className="size-4" />
              {c.label}
            </div>
            <p className="mt-2 text-2xl font-extrabold">{isLoading ? "…" : c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {[
          { title: "Ən çox baxılan səhifələr", list: top(rows, (r) => r.path) },
          {
            title: "Mənbələr",
            list: top(rows, (r) => {
              if (!r.referrer) return "Birbaşa";
              try {
                return new URL(r.referrer).hostname;
              } catch {
                return "Digər";
              }
            }),
          },
          { title: "Platformalar", list: top(rows, (r) => r.platform) },
        ].map((box) => (
          <div key={box.title} className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <p className="mb-3 text-sm font-bold">{box.title}</p>
            {box.list.length === 0 ? (
              <p className="text-xs text-muted-foreground">Məlumat yoxdur</p>
            ) : (
              <ul className="grid gap-1.5 text-xs">
                {box.list.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">{k}</span>
                    <span className="shrink-0 font-bold">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-card">
        <p className="mb-3 text-sm font-bold">Son 7 gün ({week.length} baxış)</p>
        <div className="flex items-end gap-1.5">
          {Array.from({ length: 7 }, (_, i) => {
            const dayStart = new Date();
            dayStart.setHours(0, 0, 0, 0);
            dayStart.setDate(dayStart.getDate() - (6 - i));
            const count = rows.filter((r) => {
              const t = new Date(r.created_at).getTime();
              return t >= dayStart.getTime() && t < dayStart.getTime() + 864e5;
            }).length;
            const max = Math.max(1, ...Array.from({ length: 7 }, () => count));
            return (
              <div key={i} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t bg-primary"
                  style={{ height: `${8 + (count / max) * 80}px` }}
                />
                <span className="text-[10px] text-muted-foreground">
                  {dayStart.toLocaleDateString("az-AZ", { day: "2-digit", month: "2-digit" })}
                </span>
                <span className="text-[10px] font-bold">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
